import React from "react";
import {
  GalleryVerticalEnd,
  PackageCheck,
  ShieldCheck,
  SquarePen,
  TypeOutline,
  UserRound,
} from "lucide-react";
import { Layers, Cable } from "lucide-react";
import { GeneralInfoSkeleton } from "./GeneralInfoSkeleton";

type ViewProps = {
  pluginName: string;
  version: string;
  tags: string[];
  description1: string;
  description2: string;
  ls_version: { minimum: string };
  type: string;
  author: string;
  productType: string;
  ls_product_type: string[];
  pluginDependency: { name: string; id: string }[];
  pluginLogo?: string;
  onEdit: () => void;
  isNewPlugin: boolean;
};

export const GeneralInfoView: React.FC<ViewProps> = ({
  pluginName,
  version,
  tags,
  description1,
  description2,
  ls_version,
  type,
  author,
  productType,
  pluginDependency,
  pluginLogo,
  ls_product_type,
  onEdit,
  isNewPlugin,
}: ViewProps) => {
  const isDependency = pluginDependency?.some((item) => item.name);

  const loading = !isNewPlugin && !pluginName;

  if (loading) {
    return <GeneralInfoSkeleton />;
  }

  return (
    <>
      <div className="bg-background rounded-lg shadow-sm flex flex-col md:flex-row overflow-hidden cursor-default">
        <div className="flex-1 p-6 space-y-5">
          <div className="flex justify-between items-start">
            <div className="flex items-start gap-5">
              <img
                src={`${pluginLogo || process.env.STATIC_PATH + "/images/plugin-default.jpg"}`}
                alt={`${pluginName} logo`}
                className="w-[150px] h-[150px] object-cover rounded-xl border shadow-sm"
              />
              <div className="w-full pb-2">
                <p className="text-[20px] font-extrabold text-gray-700 truncate [text-shadow:0_2px_4px_rgba(0,0,0,0.1)]">
                  {pluginName}
                </p>

                <div className="text-gray-500 mt-1 flex flex-wrap gap-3 items-center border-b pb-2 pr-2 w-fit">
                  <span
                    className="flex text-sm items-center gap-2 p-2 px-4 shadow rounded-md min-w-[150px] max-w-[200px] truncate"
                    title={author}
                  >
                    <UserRound
                      size={22}
                      className="text-primary flex-shrink-0"
                    />
                    <span className="truncate">{author}</span>
                  </span>
                  <span className="flex text-sm items-center gap-2 p-2 px-4 shadow rounded-md min-w-[60px]">
                    <GalleryVerticalEnd
                      size={19}
                      className="text-primaryGreen"
                    />{" "}
                    {version}
                  </span>
                  <span className="flex text-sm items-center gap-2 p-2 px-4 shadow rounded-md min-w-[100px]">
                    <TypeOutline size={19} className="text-primaryBlue" />{" "}
                    {type}
                  </span>
                </div>

                {/* Tags */}
                <div className="flex flex-col mt-2 max-w-[80%]">
                  <p className="text-xs font-bold uppercase text-muted-foreground pb-1 ml-1">
                    tag{tags.length > 1 ? "s" : ""}
                  </p>
                  <div className="flex flex-wrap gap-2 mt-1 px-1">
                    {tags.map((tag, i) => (
                      <span
                        key={i}
                        className="px-3 py-2 bg-lightBlue text-primaryBlue rounded-full text-xs"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-between items-center pt-2">
              <div
                onClick={onEdit}
                className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer hover:text-primaryGreen transition-colors"
              >
                <SquarePen size={18} />
              </div>
            </div>
          </div>

          {/* Product Type & LS Version */}
          <div className="flex flex-wrap gap-6 text-gray-600 text-sm font-medium">
            <span className="flex items-center gap-2 shadow p-2 px-4 rounded-lg text-xs text-primary">
              <ShieldCheck size={22} className="text-primary" />
              LS Version:{" "}
              <span className="text-muted-foreground">
                {ls_version.minimum}
              </span>
            </span>
            <span className="flex items-center gap-2 shadow p-2 px-4 rounded-lg text-xs text-primaryBlue">
              <Layers size={20} className="text-primaryBlue" />
              Product Type:{""}{" "}
              <span className="text-muted-foreground">{productType}</span>
            </span>
            <span className="flex items-center gap-2 shadow p-2 px-4 rounded-lg text-xs text-primaryGreen">
              <PackageCheck size={22} className="text-primaryGreen " />
              LS Product Type:{""}
              {ls_product_type.map((item) => (
                <span
                  key={item}
                  className="px-3 py-1.5 bg-border text-muted-foreground rounded-md text-xs uppercase"
                >
                  {item}
                </span>
              ))}
            </span>

            {isDependency && (
              <span className="flex items-center gap-2 shadow p-2 px-4 rounded-lg text-xs text-primaryGreen">
                <Cable size={20} className="text-amber " />
                <span className="text-amber">Dependency:{""}</span>
                {pluginDependency.map((dep) => (
                  <div key={dep.id} className="flex items-center gap-1">
                    <span className="text-sm text-muted-foreground">
                      {dep.name}
                    </span>
                  </div>
                ))}
              </span>
            )}
          </div>

          {/* Description Cards */}
          <div className="grid md:grid-cols-2 gap-5 !mt-7">
            <div className="bg-muted  p-4 rounded-xl shadow-sm min-h-36">
              <h4 className="text-gray-500 font-semibold mb-1 border-b pb-1">
                Plugin Description
              </h4>
              <p className="text-gray-800 text-sm break-words whitespace-pre-wrap leading-relaxed">
                {description1}
              </p>
            </div>
            <div className="bg-muted p-4 rounded-xl shadow-sm min-h-36">
              <h4 className="text-gray-500 font-semibold mb-1 border-b pb-1">
                Developer Description
              </h4>
              <p className="text-gray-800 text-sm break-words whitespace-pre-wrap leading-relaxed">
                {description2}
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
